import { AuthMachineInterpreter } from '@/apps/auth/authMachine'
import { getAuthService } from '@/apps/auth/service'
import { defineStore } from 'pinia'
import { ref, computed } from 'vue'

export const useAuthStore = defineStore('authStore', () => {
  const isLoggedIn = ref(false)
  const userEmail = ref('')
  const isSuperuser = ref(false)
  const isStaff = ref(false)
  const hasErrors = ref(false)

  // Computed property for isLoggedOut
  const isLoggedOut = computed(() => !isLoggedIn.value)

  let authCheckResolve: (() => void) | undefined
  const authCheck = new Promise<void>((resolve) => {
    authCheckResolve = resolve
  })

  let authService: AuthMachineInterpreter | null = null

  if (process.client) {
    authService = getAuthService()
    const authServiceState = authService.state
    if (authServiceState.matches('loggedIn')) {
      isLoggedIn.value = true
      userEmail.value = authServiceState.context?.email || ''
      isSuperuser.value = authServiceState.context?.isSuperuser || false
      isStaff.value = authServiceState.context?.isStaff || false
      authCheckResolve?.()
    } else if (authServiceState.matches('loggedOut')) {
      isLoggedIn.value = false
      authCheckResolve?.()
    } else if (authServiceState.matches('loggedOut.error')) {
      hasErrors.value = true
      authCheckResolve?.()
    }
    // this double if is so the transition state doesn't trigger
    authService.onTransition((state) => {
      if (state.matches('loggedIn')) {
        isLoggedIn.value = true
        hasErrors.value = false
        authCheckResolve?.()
      } else if (state.matches('loggedOut')) {
        isLoggedIn.value = false
        hasErrors.value = false
        authCheckResolve?.()
      } else if (state.matches('loggedOut.error')) {
        isLoggedIn.value = false
        hasErrors.value = true
        authCheckResolve?.()
      }
      userEmail.value = state.context?.email || ''
      isSuperuser.value = state.context?.isSuperuser || false
      isStaff.value = state.context?.isStaff || false
    })
  }

  function logOutInPlace() {
    authService?.send('LOG_OUT')
  }

  const router = useRouter()
  const { $isDesktop, $puLocalePath } = useNuxtApp()
  function logOut(redirectTo?: string) {
    authService?.send('LOG_OUT')
    try {
      if (redirectTo) {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises -- Fire and forget navigation
        router.push(redirectTo)
      } else if ($isDesktop) {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises -- Fire and forget navigation
        router.push($puLocalePath({ name: 'accounts-login' }))
      }
    } catch (error) {
      console.error(
        'Error during logout redirection:',
        error || 'Unknown error',
      )
    }
  }

  return {
    isLoggedIn,
    isLoggedOut,
    hasErrors,
    authCheckResolve,
    authCheck,
    logOut,
    logOutInPlace,
    userEmail,
    isSuperuser,
    isStaff,
  }
})
